import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import ReactSVG from 'react-svg';

import ConfirmModal from 'components/ConfirmModal';
import Tooltip from 'components/Tooltip';
import { LoaderInline } from 'components/Styled/Loader';
import { fileStatus } from 'hooks/useReport';

const Content = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
`;

const Icon = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-right: 10px;
`;

const ErrorInfoIcon = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 14px;
	height: 14px;
	margin-left: 8px;
	border-radius: 50%;
	border: 1px solid #9aa4b2;
	color: #9aa4b2;
	font-size: 10px;
	line-height: 1;
	font-weight: 700;
	cursor: default;
`;

const FileName = styled.span`
	display: inline-block;
	min-width: 0;
	flex: 1 1 auto;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const DeleteButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	margin-left: 8px;
	padding: 0;
	background: transparent;
	border: none;
	color: #9aa4b2;
	cursor: pointer;
`;

const actionType = {
	delete: 'delete',
	cancel: 'cancel',
};

const FileLabel = ({ data, onDelete, onCancel }) => {
	const [confirmAction, setConfirmAction] = useState(null);

	const handleClick = () => {
		if (data.download_url) {
			window.open(data.download_url, '_blank');
		}
	};

	const errorTooltip = data.error || 'Деталі помилки недоступні';
	const canDelete = Boolean(
		data?.fileId
		&& typeof onDelete === 'function'
		&& [fileStatus.completed, fileStatus.error].includes(data.status),
	);
	const canCancel = Boolean(
		data?.fileId
		&& typeof onCancel === 'function'
		&& [fileStatus.pending, fileStatus.processing].includes(data.status),
	);
	const canHandleAction = canDelete || canCancel;

	const handleDeleteClick = (event) => {
		event.preventDefault();
		event.stopPropagation();

		if (!canHandleAction) {
			return;
		}

		setConfirmAction(canCancel ? actionType.cancel : actionType.delete);
	};

	const handleDeleteConfirm = () => {
		if (confirmAction === actionType.cancel) {
			onCancel(data.fileId);
		} else {
			onDelete(data.fileId);
		}

		setConfirmAction(null);
	};

	const handleDeleteCancel = () => {
		setConfirmAction(null);
	};

	const actionTitle = canCancel ? 'Скасувати формування' : 'Видалити зі списку';
	const confirmText = confirmAction === actionType.cancel
		? 'Скасувати формування звіту?'
		: 'Видалити звіт зі списку?';

	return (
		<>
			<Content onClick={handleClick}>
				<Icon>
					{data.status === fileStatus.completed && (
						<ReactSVG src="data/svg/check-circle.svg" />
					)}

					{data.status === fileStatus.error && (
						<ReactSVG src="data/svg/cross-circle.svg" />
					)}

					{(data.status === fileStatus.pending || data.status === fileStatus.processing) && (
						<LoaderInline />
					)}
				</Icon>

				<FileName>{data.fileName}</FileName>

				{data.status === fileStatus.error && (
					<Tooltip position="left" tooltip={errorTooltip} interactive>
						<ErrorInfoIcon data-testid="file-error-info-icon">i</ErrorInfoIcon>
					</Tooltip>
				)}

				{canHandleAction && (
					<DeleteButton
						type="button"
						data-testid="file-delete-action"
						title={actionTitle}
						onClick={handleDeleteClick}
					>
						<i className="icon-close" />
					</DeleteButton>
				)}
			</Content>

			{confirmAction && (
				<ConfirmModal
					onClose={handleDeleteCancel}
					primaryButton
					secondaryButton
					contentModalText={confirmText}
					primaryButtonText="ТАК"
					secondaryButtonText="НІ"
					onClickPrimaryButton={handleDeleteConfirm}
					onClickSecondaryButton={handleDeleteCancel}
				/>
			)}
		</>
	);
};

FileLabel.propTypes = {
	data: PropTypes.shape({
		status: PropTypes.oneOf([
			fileStatus.completed,
			fileStatus.error,
			fileStatus.pending,
			fileStatus.processing,
		]).isRequired,
		fileId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
		fileName: PropTypes.string.isRequired,
		download_url: PropTypes.string,
		error: PropTypes.string,
	}),
	onDelete: PropTypes.func,
	onCancel: PropTypes.func,
};

export default FileLabel;
